import React, { useMemo, useState, useEffect } from 'react';
import { SeoReport, Category, Issue, Severity } from '../types';
import { t, isRTL, getTextDirection } from '../services/i18n';
import { formatDate, formatNumber } from '../src/utils/i18nFormatters';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Info, 
  ChevronDown, 
  ChevronUp, 
  Layout, 
  Cpu, 
  Globe, 
  Bot,
  ListTodo,
  Code,
  Copy,
  Check,
  Zap,
  Printer,
  Download,
  RefreshCw,
  Link2,
  FileSearch,
  Shield,
  Eye,
  BarChart3,
  Sparkles,
  Image as ImageIcon,
  Heading,
  FileText,
  Search,
  Smartphone,
  Lock,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip } from 'recharts';
import { HeroScoreSection } from './HeroScoreSection';
import { SummaryCards } from './SummaryCards';
import { CategoryScoreGrid } from './CategoryScoreGrid';
import { IssueCard } from './IssueCard';
import { ActionPlanSection } from './ActionPlanSection';

interface ResultsDashboardProps {
  report: SeoReport;
  onRescan?: () => void;
  onNewScan?: () => void;
}

// Translation helpers
const getCategoryName = (category: Category | string): string => {
  switch (category) {
    case Category.ON_PAGE:
      return t('results.categoryOnPage') || 'On-Page SEO';
    case Category.TECHNICAL:
      return t('results.categoryTechnical') || 'Technical SEO';
    case 'Performance':
      return t('results.performance') || 'Performance';
    case 'Mobile':
      return t('results.mobile') || 'Mobile';
    default:
      return String(category);
  }
};

const CategoryIcon = ({ category }: { category: Category }) => {
  switch (category) {
    case Category.ON_PAGE: return <Layout className="w-5 h-5" />;
    case Category.TECHNICAL: return <Cpu className="w-5 h-5" />;
    default: return <Layout className="w-5 h-5" />;
  }
};

const SeverityBadge = ({ severity }: { severity: Severity }) => {
  const colors = {
    [Severity.CRITICAL]: 'bg-red-500/100/10 text-red-600 border-red-500/30',
    [Severity.HIGH]: 'bg-orange-500/10 text-orange-400 border-orange-200',
    [Severity.MEDIUM]: 'bg-yellow-500/100/10 text-yellow-600 border-yellow-500/30',
    [Severity.LOW]: 'bg-blue-500/100/10 text-blue-400 border-blue-500/30',
    [Severity.INFO]: 'bg-slate-100 text-slate-300 border-slate-600',
  };

  const labels = {
    [Severity.CRITICAL]: t('results.severityCritical') || 'Critical',
    [Severity.HIGH]: t('results.severityHigh') || 'High',
    [Severity.MEDIUM]: t('results.severityMedium') || 'Medium',
    [Severity.LOW]: t('results.severityLow') || 'Low',
    [Severity.INFO]: t('results.severityInfo') || 'Info',
  };

  return (
    <span className={`px-2.5 py-1 rounded-md text-[10px] uppercase font-bold border tracking-wider ${colors[severity]}`}>
      {labels[severity]}
    </span>
  );
};

const SnippetView = ({ code }: { code: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Sanitize code to prevent XSS attacks
  // Escape HTML entities to prevent script execution
  const sanitizeCode = (code: string): string => {
    const div = document.createElement('div');
    div.textContent = code; // textContent automatically escapes HTML
    return div.innerHTML; // Get escaped HTML
  };

  const sanitizedCode = sanitizeCode(code);

  return (
    <div className="relative bg-slate-900 rounded-lg p-4 border border-slate-700 print:bg-slate-700/50 print:border-slate-300">
      <pre className="text-xs text-slate-300 print:text-slate-200 font-mono whitespace-pre-wrap break-words">
        <code dangerouslySetInnerHTML={{ __html: sanitizedCode }} />
      </pre>
        <button 
          onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 bg-slate-800/80 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors no-print print:hidden"
        title={t('results.copyCode') || 'Copy code'}
        >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
  );
};

// IssueCard is now imported from separate component file

export const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ report, onRescan, onNewScan }) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  
  // RTL detection
  const [isRtlMode, setIsRtlMode] = useState(false);
  
  useEffect(() => {
    const direction = getTextDirection();
    setIsRtlMode(direction === 'rtl');
    // Ensure document direction is set
    document.documentElement.setAttribute('dir', direction);
    
    // Listen for language change events to update RTL mode
    const handleLanguageChange = () => {
      const newDirection = getTextDirection();
      setIsRtlMode(newDirection === 'rtl');
      document.documentElement.setAttribute('dir', newDirection);
    };
    
    window.addEventListener('languageChange', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);
  
  // Validate report structure
  if (!report || typeof report.overallScore !== 'number') {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center" dir={isRtlMode ? 'rtl' : 'ltr'}>
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-red-200 mb-2">Invalid Report Data</h3>
        <p className="text-red-600">The analysis response is missing required fields. Please try again.</p>
    </div>
  );
  }
  
  // [DEBUG] Step 5: Comprehensive Screenshot Search
  // Get comprehensive data and screenshots
  const comprehensive = (report as any).comprehensive;
  
  // COMPREHENSIVE DEBUG LOGGING - Show full structure
  console.log('[ResultsDashboard] Screenshot search:', {
    reportKeys: Object.keys(report),
    comprehensiveKeys: comprehensive ? Object.keys(comprehensive) : [],
    hasReportScreenshot: !!(report as any).screenshot,
    hasComprehensiveScreenshot: !!comprehensive?.screenshot,
    hasReportComprehensiveScreenshot: !!(report as any)?.comprehensive?.screenshot,
    reportScreenshotType: typeof (report as any).screenshot,
    reportScreenshotLength: (report as any).screenshot?.length || 0,
    comprehensiveScreenshotType: typeof comprehensive?.screenshot,
    comprehensiveScreenshotLength: comprehensive?.screenshot?.length || 0,
    // Show actual report structure (limited to avoid huge logs)
    reportStructure: {
      keys: Object.keys(report),
      hasComprehensive: !!(report as any).comprehensive,
      comprehensiveType: typeof (report as any).comprehensive,
      comprehensiveKeys: (report as any).comprehensive ? Object.keys((report as any).comprehensive) : null,
      // Check all possible screenshot locations
      screenshotLocations: {
        'report.screenshot': !!(report as any).screenshot,
        'report.comprehensive.screenshot': !!(report as any)?.comprehensive?.screenshot,
        'comprehensive.screenshot': !!comprehensive?.screenshot,
        'report.mobileScreenshot': !!(report as any).mobileScreenshot,
        'report.comprehensive.mobileScreenshot': !!(report as any)?.comprehensive?.mobileScreenshot,
        'comprehensive.mobileScreenshot': !!comprehensive?.mobileScreenshot
      }
    }
  });
  
  // Also log the actual report object structure (first level only)
  const reportStructure = Object.keys(report).reduce((acc, key) => {
    const value = (report as any)[key];
    acc[key] = {
      type: typeof value,
      isArray: Array.isArray(value),
      isObject: typeof value === 'object' && value !== null && !Array.isArray(value),
      keys: typeof value === 'object' && value !== null ? Object.keys(value).slice(0, 10) : null,
      hasScreenshot: key === 'comprehensive' ? !!(value as any)?.screenshot : (key === 'screenshot' ? true : false),
      isScreenshot: key === 'screenshot' || key === 'mobileScreenshot'
    };
    return acc;
  }, {} as any);
  
  console.log('[ResultsDashboard] Full report structure (first level):', reportStructure);
  
  // CRITICAL: Check if 'screenshot' key exists in report (even if value is null/undefined)
  if (!('screenshot' in report) && !('mobileScreenshot' in report)) {
    console.error('[ResultsDashboard] ❌ CRITICAL: screenshot and mobileScreenshot keys do not exist in report object at all!');
    console.error('[ResultsDashboard] Available keys:', Object.keys(report));
    console.error('[ResultsDashboard] This means screenshots were never attached to the report on the backend!');
  } else {
    console.log('[ResultsDashboard] ✅ Screenshot keys exist in report:', {
      hasScreenshotKey: 'screenshot' in report,
      hasMobileScreenshotKey: 'mobileScreenshot' in report,
      screenshotValue: (report as any).screenshot ? 'has value' : 'null/undefined',
      mobileScreenshotValue: (report as any).mobileScreenshot ? 'has value' : 'null/undefined'
    });
  }
  
  // Try to find screenshot in order of preference
  let screenshot: string | null = null;
  let mobileScreenshot: string | null = null;
  
  // Path 1: Direct on report
  if ((report as any).screenshot && typeof (report as any).screenshot === 'string' && (report as any).screenshot.length > 0) {
    screenshot = (report as any).screenshot;
    console.log('[ResultsDashboard] ✓ Found screenshot on report (length:', screenshot.length, ')');
  }
  
  // Path 2: On comprehensive
  if (!screenshot && comprehensive?.screenshot && typeof comprehensive.screenshot === 'string' && comprehensive.screenshot.length > 0) {
    screenshot = comprehensive.screenshot;
    console.log('[ResultsDashboard] ✓ Found screenshot on comprehensive (length:', screenshot.length, ')');
  }
  
  // Path 3: Nested comprehensive
  if (!screenshot && (report as any)?.comprehensive?.screenshot && typeof (report as any).comprehensive.screenshot === 'string' && (report as any).comprehensive.screenshot.length > 0) {
    screenshot = (report as any).comprehensive.screenshot;
    console.log('[ResultsDashboard] ✓ Found screenshot on report.comprehensive (length:', screenshot.length, ')');
  }
  
  // Mobile screenshot search
  if ((report as any).mobileScreenshot && typeof (report as any).mobileScreenshot === 'string' && (report as any).mobileScreenshot.length > 0) {
    mobileScreenshot = (report as any).mobileScreenshot;
    console.log('[ResultsDashboard] ✓ Found mobile screenshot on report');
  }
  if (!mobileScreenshot && comprehensive?.mobileScreenshot && typeof comprehensive.mobileScreenshot === 'string' && comprehensive.mobileScreenshot.length > 0) {
    mobileScreenshot = comprehensive.mobileScreenshot;
    console.log('[ResultsDashboard] ✓ Found mobile screenshot on comprehensive');
  }
  if (!mobileScreenshot && (report as any)?.comprehensive?.mobileScreenshot && typeof (report as any).comprehensive.mobileScreenshot === 'string' && (report as any).comprehensive.mobileScreenshot.length > 0) {
    mobileScreenshot = (report as any).comprehensive.mobileScreenshot;
    console.log('[ResultsDashboard] ✓ Found mobile screenshot on report.comprehensive');
  }
  
  if (!screenshot) {
    console.error('[ResultsDashboard] ❌ No screenshot found anywhere:', {
      reportStructure: JSON.stringify({
        keys: Object.keys(report),
        hasScreenshot: !!(report as any).screenshot,
        hasComprehensive: !!(report as any).comprehensive,
        comprehensiveKeys: (report as any).comprehensive ? Object.keys((report as any).comprehensive) : []
      }, null, 2).substring(0, 1000)
    });
  }
  const reorganized = (report as any).reorganized; // AI-reorganized audit results
  
  // Calculate Performance and Mobile scores from report data
  const performanceScore = useMemo(() => {
    if (!report.performanceAudit) return 0;
    const minification = report.performanceAudit.minification;
    const cacheability = report.performanceAudit.cacheability;
    let score = 100;
    
    // Deduct for unminified files
    if (minification) {
      const unminifiedCSS = minification.css?.filter((f: any) => !f.isMinified).length || 0;
      const unminifiedJS = minification.js?.filter((f: any) => !f.isMinified).length || 0;
      score -= (unminifiedCSS + unminifiedJS) * 2;
    }
    
    // Deduct for uncached assets
    if (cacheability && cacheability.uncached > 0) {
      score -= Math.min(cacheability.uncached * 3, 20);
    }
    
    return Math.max(0, Math.min(100, score));
  }, [report.performanceAudit]);

  const mobileScore = useMemo(() => {
    let score = 100;
    const viewport = comprehensive?.mobile?.viewport || comprehensive?.technical?.viewport;
    
    // Check viewport
    if (!viewport || !viewport.present || !viewport.exists) {
      score -= 30;
    } else if (viewport.content && !viewport.content.toLowerCase().includes('width=device-width')) {
      score -= 15;
    }
    
    // Check responsive images
    const images = comprehensive?.images;
    if (images && images.total > 0) {
      const responsiveRatio = (images.withSrcset || 0) / images.total;
      if (responsiveRatio < 0.5) score -= 10;
    }
    
    return Math.max(0, Math.min(100, score));
  }, [comprehensive]);

  // Ensure categories exist with defaults (On-Page, Technical, Performance, Mobile)
  const safeCategories: Record<string, { score: number; totalIssues: number; criticalIssues: number }> = {
    [Category.ON_PAGE]: report.categories?.[Category.ON_PAGE] || { score: 0, totalIssues: 0, criticalIssues: 0 },
    [Category.TECHNICAL]: report.categories?.[Category.TECHNICAL] || { score: 0, totalIssues: 0, criticalIssues: 0 },
    'Performance': { 
      score: performanceScore, 
      totalIssues: (report.performanceAudit?.minification?.css?.filter((f: any) => !f.isMinified).length || 0) + 
                   (report.performanceAudit?.minification?.js?.filter((f: any) => !f.isMinified).length || 0) +
                   (report.performanceAudit?.cacheability?.uncached || 0),
      criticalIssues: 0 
    },
    'Mobile': { 
      score: mobileScore, 
      totalIssues: (!comprehensive?.mobile?.viewport?.present && !comprehensive?.technical?.viewport ? 1 : 0),
      criticalIssues: (!comprehensive?.mobile?.viewport?.present && !comprehensive?.technical?.viewport ? 1 : 0)
    },
  };

  // Filter out categories with 0 score AND 0 issues AND no data
  const visibleCategories = useMemo(() => {
    return Object.entries(safeCategories).filter(([category, data]) => {
      // Show category if it has a score > 0 OR has issues OR has comprehensive data
      const hasData = category === Category.ON_PAGE && comprehensive?.headings ||
                     category === Category.TECHNICAL && comprehensive?.technical ||
                     category === 'Performance' && report.performanceAudit ||
                     category === 'Mobile' && comprehensive?.mobile;
      return data.score > 0 || data.totalIssues > 0 || hasData;
    });
  }, [safeCategories, comprehensive, report.performanceAudit]);

  const safeIssues = Array.isArray(report.issues) ? report.issues : [];
  const safeKeywords = Array.isArray(report.keywordsDetected) ? report.keywordsDetected : [];
  const safeUrl = report.url || 'Unknown URL';
  const safeSummary = report.summary || 'No summary available.';

  const chartData = useMemo(() => {
    return visibleCategories.map(([cat, data]) => ({
      subject: getCategoryName(cat as Category),
      A: data.score,
      fullMark: 100
    }));
  }, [visibleCategories]);

  const [filter, setFilter] = useState<'all' | Category | 'p0' | 'p1' | 'p2' | 'p3'>('all');
  const [imageFilter, setImageFilter] = useState<'all' | 'missingAlt' | 'nonWebp' | 'webp'>('all');
  // All sections collapsed by default for better organization
  // All sections collapsed by default for cleaner, more organized view
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    onPage: false,
    technical: false,
    performance: false,
    links: false,
    actionPlan: false,
    radar: false,
    reorganized: false
  });
  const [expandedHeadings, setExpandedHeadings] = useState<Record<string, boolean>>({
    h3: false,
    h4: false,
    h5: false,
    h6: false
  });

  const filteredIssues = useMemo(() => {
    if (filter === 'all') return safeIssues;
    if (filter === 'p0') {
      return safeIssues.filter((i: any) => i.priority === 'P0' || i.severity === 'Critical');
    }
    if (filter === 'p1') {
      return safeIssues.filter((i: any) => i.priority === 'P1' || (i.severity === 'High' && i.priority !== 'P0'));
    }
    if (filter === 'p2') {
      return safeIssues.filter((i: any) => i.priority === 'P2' || (i.severity === 'Medium' && !i.priority));
    }
    if (filter === 'p3') {
      return safeIssues.filter((i: any) => i.priority === 'P3' || (i.severity === 'Low' && !i.priority));
    }
    return safeIssues.filter(i => i.category === filter);
  }, [safeIssues, filter]);

  const sortedIssues = useMemo(() => {
    // Sort by priority first (P0 > P1 > P2 > P3), then by severity
    const priorityOrder = { 'P0': 0, 'P1': 1, 'P2': 2, 'P3': 3 };
    const severityOrder = {
      [Severity.CRITICAL]: 0,
      [Severity.HIGH]: 1,
      [Severity.MEDIUM]: 2,
      [Severity.LOW]: 3,
      [Severity.INFO]: 4
    };
    return [...filteredIssues].sort((a, b) => {
      const aPriority = (a as any).priority ? priorityOrder[(a as any).priority as keyof typeof priorityOrder] : 999;
      const bPriority = (b as any).priority ? priorityOrder[(b as any).priority as keyof typeof priorityOrder] : 999;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }, [filteredIssues]);

  const [generatingPDF, setGeneratingPDF] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const scrollToSection = (category: Category | string) => {
    const sectionMap: Record<string, string> = {
      [Category.ON_PAGE]: 'onPage',
      [Category.TECHNICAL]: 'technical',
      'Performance': 'performance',
      'Mobile': 'technical', // Mobile info is in Technical section
      [Category.PERFORMANCE]: 'performance',
      [Category.MOBILE]: 'technical',
      [Category.LINK_ANALYSIS]: 'links',
    };
    
    const sectionId = sectionMap[category] || sectionMap[String(category)];
    if (sectionId) {
      // Expand the section first
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: true
      }));
      
      // Scroll to section after a brief delay
      setTimeout(() => {
        const element = document.getElementById(`section-${sectionId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadJson = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(report, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    const dateStr = formatDate(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
    downloadAnchorNode.setAttribute("download", `orca-report-${dateStr}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleDownloadPDF = async () => {
    setGeneratingPDF(true);
    try {
      const { authService } = await import('../services/authService');
      const blob = await authService.generatePDFReport(report);
      const url = window.URL.createObjectURL(blob);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", url);
      const dateStr = formatDate(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
      downloadAnchorNode.setAttribute("download", `orca-seo-report-${safeUrl.replace(/https?:\/\//, '').replace(/\//g, '-')}-${dateStr}.pdf`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('PDF generation error:', error);
      alert(`${t('results.pdfGenerationFailed')}: ${error.message || t('results.unknownError')}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
    if (score >= 40) return 'text-yellow-600 bg-yellow-500/10 border-yellow-500/30';
    return 'text-red-600 bg-red-500/10 border-red-500/30';
  };

  // Calculate pixel width for title/meta (approximate)
  const calculatePixelWidth = (text: string): number => {
    // Approximate: average character width in Arial 20px is ~6.5px
    return Math.round(text.length * 6.5);
  };

  const handleDownload = () => {
    handleDownloadPDF();
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-12 print:bg-slate-800" dir={isRtlMode ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 1. HERO SCORE SECTION - Redesigned */}
        <HeroScoreSection
          score={report.overallScore}
          url={safeUrl}
          screenshot={screenshot}
          onRescan={onRescan}
          onNewScan={onNewScan}
          onDownload={handleDownload}
        />

        {/* 2. SUMMARY CARDS - Wins, Priority, Quick Wins */}
        <SummaryCards issues={safeIssues} />

        {/* 3. CATEGORY SCORES - Redesigned with Progress Rings */}
        {visibleCategories.length > 0 && (
          <CategoryScoreGrid 
            categories={Object.fromEntries(visibleCategories) as Record<Category, { score: number; totalIssues: number; criticalIssues: number }>}
            onCategoryClick={scrollToSection}
          />
        )}
        {/* Website Preview Card - Dark Mode Optimized */}
        {screenshot && (
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-2xl border-2 border-slate-700/50 p-6 mb-6 print:shadow-none print:border-slate-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Eye className="w-5 h-5 text-indigo-400" />
          </div>
              <h2 className="text-xl font-bold text-white">{t('results.websitePreview') || 'Website Preview'}</h2>
            </div>
            <div className="rounded-lg overflow-hidden border-2 border-slate-600/50 bg-slate-900/50 p-3 shadow-inner">
              <img 
                src={typeof screenshot === 'string' && screenshot.startsWith('data:') 
                  ? screenshot 
                  : `data:image/png;base64,${screenshot}`} 
                alt={t('results.websitePreview') || 'Website preview'} 
                className="w-full h-auto max-h-[500px] object-contain rounded-md shadow-lg"
                style={{ 
                  filter: 'brightness(0.95) contrast(1.05)',
                  imageRendering: 'crisp-edges'
                }}
                onError={(e) => {
                  console.error('[ResultsDashboard] Screenshot failed to load:', e);
                  // Hide the image container on error
                  const target = e.target as HTMLImageElement;
                  if (target.parentElement) {
                    target.parentElement.style.display = 'none';
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Summary Card - Enhanced with AI Analysis */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-indigo-500/30 p-6 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-500/20 rounded-lg animate-pulse">
              <Bot className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('results.summaryLabel') || 'AI-Powered Summary'}</h2>
              <p className="text-xs text-indigo-400/80 font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {t('results.expertAnalysis') || 'Expert Analysis by Orca AI'}
              </p>
            </div>
          </div>
          {/* Enhanced Summary with Details */}
          <div className="space-y-4">
            {safeSummary ? (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-lg p-5 border border-indigo-500/20 shadow-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0 animate-pulse" />
                  <div className="flex-1">
                    <p className="text-slate-100 leading-relaxed text-sm font-medium">{safeSummary}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-lg p-5 border border-indigo-500/20 shadow-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-400 mt-0.5 flex-shrink-0 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    {safeIssues.length > 0 ? (
                      <>
                        <p className="text-slate-100 leading-relaxed text-sm">
                          <span className="text-indigo-300 font-semibold">📊 {t('results.expertAnalysis') || 'Analysis Results'}:</span>{' '}
                          {t('results.foundIssues', { 
                            count: safeIssues.length.toString(), 
                            issueText: safeIssues.length === 1 ? (t('results.issue') || 'issue') : (t('results.issues') || 'issues')
                          }) || `Found ${safeIssues.length} SEO ${safeIssues.length === 1 ? 'issue' : 'issues'}`}
                        </p>
                        {(() => {
                          const onPageIssues = safeIssues.filter(i => i.category === Category.ON_PAGE);
                          const technicalIssues = safeIssues.filter(i => i.category === Category.TECHNICAL);
                          const criticalHigh = safeIssues.filter(i => i.severity === Severity.CRITICAL || i.severity === Severity.HIGH).length;
                          const findings: React.ReactNode[] = [];
                          
                          if (criticalHigh > 0) {
                            findings.push(
                              <span key="critical" className="text-red-400 font-semibold">
                                {t('results.priorityIssue', { count: String(criticalHigh), plural: criticalHigh > 1 ? 's' : '' }) || `${criticalHigh} priority issue${criticalHigh > 1 ? 's' : ''} requiring immediate attention`}
                              </span>
                            );
                          }
                          
                          if (onPageIssues.length > 0) {
                            const imageIssues = onPageIssues.filter(i => i.title?.toLowerCase().includes('image') || i.title?.toLowerCase().includes('alt')).length;
                            if (imageIssues > 0) {
                              findings.push(
                                <span key="images" className="text-yellow-400">
                                  {t('results.imagesMissingAltTextCount', { count: String(imageIssues), plural: imageIssues > 1 ? 's' : '' }) || `${imageIssues} image${imageIssues > 1 ? 's' : ''} missing alt text`}
                                </span>
                              );
                            }
                          }
                          
                          if (technicalIssues.length > 0) {
                            const missingCanonical = technicalIssues.filter(i => i.title?.toLowerCase().includes('canonical')).length;
                            const missingViewport = technicalIssues.filter(i => i.title?.toLowerCase().includes('viewport')).length;
                            const techParts: string[] = [];
                            if (missingCanonical > 0) techParts.push(t('results.missingCanonicalTag') || 'missing canonical tag');
                            if (missingViewport > 0) techParts.push(t('results.missingViewportTag') || 'missing viewport meta tag');
                            if (techParts.length > 0) {
                              findings.push(
                                <span key="technical" className="text-orange-400">
                                  {techParts.join(', ')}
                                </span>
                              );
                            }
                          }
                          
                          return findings.length > 0 ? (
                            <p className="text-slate-200 leading-relaxed text-sm">
                              <span className="text-indigo-300 font-medium">{t('results.keyFindings') || 'Key findings:'}</span>{' '}
                              {findings.map((finding, idx) => (
                                <React.Fragment key={idx}>
                                  {finding}
                                  {idx < findings.length - 1 && ', '}
                                </React.Fragment>
                              ))}
                              .
                            </p>
                          ) : null;
                        })()}
                        <p className="text-slate-200 leading-relaxed text-sm mt-2">
                          {safeIssues.length <= 5 ? (
                            <span className="text-emerald-300">
                              ✅ <strong>{t('results.goodNews') || 'Good news'}:</strong> {t('results.goodNewsMessage') || 'Your website has solid SEO fundamentals. Focus on fixing the priority issues above to improve your score.'}
                            </span>
                          ) : (
                            <span className="text-yellow-300">
                              ⚠️ <strong>{t('results.actionNeeded') || 'Action needed'}:</strong> {t('results.actionNeededMessage') || 'Review and fix the issues below to significantly improve your SEO performance and rankings.'}
                 </span>
                          )}
                        </p>
                      </>
                    ) : (
                      <p className="text-slate-100 leading-relaxed text-sm">
                        <span className="text-emerald-300">✅ {t('results.excellentMessage') || 'Excellent! No major SEO issues detected. Your website is well-optimized.'}</span>
                      </p>
               )}
            </div>
          </div>
        </div>
            )}
            
            {/* Quick Stats - Enhanced */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-700/50">
              <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-lg p-4 text-center border border-slate-700/50 hover:border-indigo-500/30 transition-all">
                <div className="text-3xl font-bold text-white mb-1">{formatNumber(safeIssues.length)}</div>
                <div className="text-xs text-slate-400 font-medium">{t('results.totalIssues') || 'Total Issues'}</div>
              </div>
              <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-lg p-4 text-center border border-red-500/30 hover:border-red-500/50 transition-all">
                <div className="text-3xl font-bold text-red-400 mb-1">
                  {formatNumber(safeIssues.filter(i => i.severity === Severity.CRITICAL || i.severity === Severity.HIGH).length)}
                </div>
                <div className="text-xs text-slate-400 font-medium">{t('results.priorityIssues') || 'Priority Issues'}</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-lg p-4 text-center border border-emerald-500/30 hover:border-emerald-500/50 transition-all">
                <div className="text-3xl font-bold text-emerald-400 mb-1">
                  {comprehensive?.images?.withAlt || 0}
                </div>
                <div className="text-xs text-slate-400 font-medium">{t('results.imagesWithAlt') || 'Images with Alt'}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-lg p-4 text-center border border-blue-500/30 hover:border-blue-500/50 transition-all">
                <div className="text-3xl font-bold text-blue-400 mb-1">
                  {formatNumber(comprehensive?.links?.internal || 0)}
                </div>
                <div className="text-xs text-slate-400 font-medium">{t('results.internalLinks') || 'Internal Links'}</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* 3. ON-PAGE SEO (Expandable) */}
        {comprehensive && (
          <div id="section-onPage" className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-8 shadow-sm">
            <button
              onClick={() => toggleSection('onPage')}
              className="w-full flex items-center justify-between mb-6 text-left"
            >
              <div className="flex items-center gap-3">
                <Layout className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white">{t('results.categoryOnPage') || 'On-Page SEO'}</h2>
        </div>
              {expandedSections.onPage ? (
                <ChevronUp className="text-slate-400" size={24} />
              ) : (
                <ChevronDown className="text-slate-400" size={24} />
              )}
            </button>
            
            {expandedSections.onPage && (
              <div className="mt-4 space-y-6">
                {/* Title Tag */}
                {comprehensive.title && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('results.titleTag') || 'Title Tag'}
                    </label>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="text-white font-medium mb-2">{comprehensive.title.text || comprehensive.title}</div>
                      <div className="flex gap-4 text-xs text-slate-400">
                        <span>{t('results.length') || 'Length'}: {comprehensive.title.length || comprehensive.title?.text?.length || 0} {t('results.characters') || 'characters'}</span>
                        {comprehensive.title.pixelWidth && (
                          <span>{t('results.pixelWidth') || 'Pixel Width'}: {comprehensive.title.pixelWidth}px</span>
                        )}
                        {comprehensive.title.isTruncated && (
                          <span className="text-red-600 font-semibold">{t('results.willBeTruncated') || 'Will be truncated in Google'}</span>
            )}
      </div>
                    </div>
                  </div>
                )}

                {/* Meta Description */}
                {comprehensive.metaDescription && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('results.metaDescription') || 'Meta Description'}
                    </label>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="text-white font-medium mb-2">{comprehensive.metaDescription.text || comprehensive.metaDescription}</div>
                      <div className="flex gap-4 text-xs text-slate-400">
                        <span>{t('results.length') || 'Length'}: {comprehensive.metaDescription.length || comprehensive.metaDescription?.text?.length || 0} {t('results.characters') || 'characters'}</span>
                        {comprehensive.metaDescription.pixelWidth && (
                          <span>{t('results.pixelWidth') || 'Pixel Width'}: {comprehensive.metaDescription.pixelWidth}px</span>
               )}
            </div>
            </div>
        </div>
                )}

                {/* Google Preview */}
                {(comprehensive.title || comprehensive.metaDescription) && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      {t('results.googlePreview') || 'Google Preview'}
                    </label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Desktop Preview */}
                      <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                          <Layout className="w-3 h-3" />
                          {t('results.desktop') || 'Desktop'}
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3">
                          <div className="text-blue-400 text-sm font-medium mb-1 line-clamp-1">
                            {comprehensive.title?.text || comprehensive.title || 'Page Title'}
                          </div>
                          <div className="text-green-400 text-xs mb-1">{safeUrl}</div>
                          <div className="text-slate-300 text-xs line-clamp-2">
                            {comprehensive.metaDescription?.text || comprehensive.metaDescription || 'Meta description text appears here...'}
                          </div>
                        </div>
                      </div>
                      {/* Mobile Preview */}
                      <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                          <Smartphone className="w-3 h-3" />
                          {t('results.mobile') || 'Mobile'}
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3">
                          {/* Favicon */}
                          {comprehensive.technical?.favicon?.url && (
                            <div className="flex items-center gap-2 mb-2">
                              <img 
                                src={comprehensive.technical.favicon.url.startsWith('http') 
                                  ? comprehensive.technical.favicon.url 
                                  : new URL(comprehensive.technical.favicon.url, safeUrl).href}
                                alt="Favicon"
                                className="w-4 h-4"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="text-blue-400 text-sm font-medium mb-1 line-clamp-2">
                            {comprehensive.title?.text || comprehensive.title || 'Page Title'}
                          </div>
                          <div className="text-green-400 text-xs mb-1 truncate">{safeUrl}</div>
                          <div className="text-slate-300 text-xs line-clamp-2">
                            {comprehensive.metaDescription?.text || comprehensive.metaDescription || 'Meta description text appears here...'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Headings Structure - ALL HEADINGS WITH FULL TEXT */}
                {comprehensive.headings && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <Heading className="w-4 h-4" />
                      {t('results.headingsStructure') || 'Headings Structure'}
                    </label>
                    
                    {/* Counts */}
                    <div className="grid grid-cols-6 gap-3 mb-4">
                      {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((tag) => (
                        <div key={tag} className="bg-slate-800 rounded-lg p-3 text-center border border-slate-600">
                          <div className="text-3xl font-bold text-white">
                            {comprehensive.headings.counts?.[tag] || 0}
          </div>
                          <div className="text-xs text-slate-400 uppercase mt-1 font-semibold">{tag}</div>
                        </div>
                      ))}
      </div>

                    {/* Display ALL H1 headings */}
                    {comprehensive.headings.h1 && comprehensive.headings.h1.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
                          &lt;H1&gt; {t('results.headings') || 'Headings'} ({comprehensive.headings.h1.length})
            </div>
                        <div className="space-y-2">
                          {comprehensive.headings.h1.map((h1: any, idx: number) => (
                            <div key={idx} className="bg-slate-800 p-3 rounded-lg border border-slate-600">
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-bold text-indigo-400 mt-1">#{idx + 1}</span>
                                <span className="text-white font-medium flex-1">{h1.text}</span>
                                <span className="text-xs text-slate-400">{h1.length || h1.text.length} {t('results.chars') || 'chars'}</span>
            </div>
        </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Display ALL H2 headings */}
                    {comprehensive.headings.h2 && comprehensive.headings.h2.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
                          &lt;H2&gt; {t('results.headings') || 'Headings'} ({comprehensive.headings.h2.length})
                        </div>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {comprehensive.headings.h2.map((h2: any, idx: number) => (
                            <div key={idx} className="bg-slate-800 p-2 rounded border border-slate-600 text-sm">
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-bold text-blue-400">#{idx + 1}</span>
                                <span className="text-slate-200 flex-1">{h2.text}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Display H3-H6 headings (collapsible) */}
                    {comprehensive.headings.h3 && comprehensive.headings.h3.length > 0 && (
                      <div className="mb-4">
                  <button 
                          onClick={() => setExpandedHeadings(prev => ({ ...prev, h3: !prev.h3 }))}
                          className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide flex items-center gap-2 hover:text-indigo-400"
                  >
                          &lt;H3&gt; {t('results.headings') || 'Headings'} ({comprehensive.headings.h3.length})
                          {expandedHeadings.h3 ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                        {expandedHeadings.h3 && (
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {comprehensive.headings.h3.map((h3: any, idx: number) => (
                              <div key={idx} className="bg-slate-800 p-2 rounded border border-slate-600 text-xs">
                                <span className="text-xs font-bold text-green-600">#{idx + 1}</span> {h3.text}
          </div>
                            ))}
                          </div>
                )}
            </div>
                    )}
                    
                    {/* H4, H5, H6 follow same pattern */}
                    {comprehensive.headings.h4 && comprehensive.headings.h4.length > 0 && (
                      <div className="mb-4">
                  <button 
                          onClick={() => setExpandedHeadings(prev => ({ ...prev, h4: !prev.h4 }))}
                          className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide flex items-center gap-2 hover:text-indigo-400"
                  >
                          &lt;H4&gt; ({comprehensive.headings.h4.length})
                          {expandedHeadings.h4 ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                        {expandedHeadings.h4 && (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {comprehensive.headings.h4.map((h4: any, idx: number) => (
                              <div key={idx} className="bg-slate-800 p-2 rounded border border-slate-600 text-xs text-slate-300">
                                #{idx + 1}: {h4.text}
                </div>
                            ))}
              </div>
            )}
          </div>
                    )}
        </div>
                )}

                {/* Content Analysis - WooRank Style Format */}
                {comprehensive.content && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('results.contentAnalysis') || 'Content Analysis'}
                    </label>
                    
                    {/* Long-tail Keywords (2-5 word phrases) - Top 7 Focused Keywords Only */}
                    {comprehensive.content.longTailKeywords && comprehensive.content.longTailKeywords.length > 0 ? (
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-indigo-300 mb-2">
                          {t('results.longTailKeywords') || 'Long-Tail Keywords (2-5 words)'} - {t('results.top7Focused') || 'Top 7 Focused'}:
      </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {comprehensive.content.longTailKeywords.slice(0, 7).map((kw: any, idx: number) => (
                            <div key={idx} className="bg-indigo-500/10 p-2 rounded border border-indigo-500/30 text-sm flex items-center justify-between">
                              <span className="font-semibold text-indigo-300">{kw.phrase || kw.keyword || ''}</span>
                              <span className="text-indigo-400/70 ml-2 font-mono">{kw.count || 0}</span>
    </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Single Keywords - WooRank Style */}
                    {comprehensive.content.keywordFrequency && comprehensive.content.keywordFrequency.length > 0 ? (
                      <div>
                        <div className="text-xs font-semibold text-slate-300 mb-2">
                          {t('results.singleKeywords') || 'Single Keywords'}:
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {comprehensive.content.keywordFrequency.map((kw: any, idx: number) => (
                            <div key={idx} className="bg-slate-800 p-2 rounded border border-slate-600 text-sm flex items-center justify-between">
                              <span className="font-semibold text-white">{kw.keyword || ''}</span>
                              <span className="text-slate-400 ml-2 font-mono">{kw.count || 0}</span>
                            </div>
                          ))}
                        </div>
                        {comprehensive.content.keywordExplanation ? (
                          <div className="mt-3 p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/30">
                            <div className="text-xs font-semibold text-indigo-300 mb-2 flex items-center gap-2">
                              <Sparkles className="w-3 h-3" />
                              {t('results.aiKeywordAnalysis') || 'AI Keyword Analysis'}
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed">
                              {comprehensive.content.keywordExplanation}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 mt-3 italic">
                            {t('results.keywordExplanation') || 'This data represents the words and phrases that your page appears to be optimized around. We use natural language processing (NLP) to do this analysis. The numbers next to each word or phrase represents how often we detected them and their variants on the page.'}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Images - Show WHICH images are missing alt + Format Analysis */}
                {comprehensive.images && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-2 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      {t('results.altAttribute') || 'Alt Attribute & Image Format'}
                    </label>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      <div className="text-sm text-slate-200 mb-2">
                        {t('results.foundImages') || 'We found'} {formatNumber(comprehensive.images.total)} {t('results.imagesOnPage') || 'image(s) on this web page'}.
          </div>
                      
                      {/* Image Filters */}
                      {comprehensive.images.allImages && comprehensive.images.allImages.length > 0 && (
                        <div className="mb-3 flex gap-2 flex-wrap">
                          <button
                            onClick={() => setImageFilter('all')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                              imageFilter === 'all' 
                                ? 'bg-indigo-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {t('results.all', { count: String(comprehensive.images.allImages.length) }) || `All (${comprehensive.images.allImages.length})`}
                          </button>
                          <button
                            onClick={() => setImageFilter('missingAlt')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                              imageFilter === 'missingAlt' 
                                ? 'bg-red-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {t('results.missingAlt', { count: String(comprehensive.images.missingAlt || 0) }) || `Missing Alt (${comprehensive.images.missingAlt || 0})`}
                          </button>
                          <button
                            onClick={() => setImageFilter('nonWebp')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                              imageFilter === 'nonWebp' 
                                ? 'bg-yellow-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {t('results.nonWebP', { count: String(comprehensive.images.nonWebp || 0) }) || `Non-WebP (${comprehensive.images.nonWebp || 0})`}
                          </button>
                          <button
                            onClick={() => setImageFilter('webp')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                              imageFilter === 'webp' 
                                ? 'bg-emerald-600 text-white' 
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {t('results.webp', { count: String(comprehensive.images.webp || 0) }) || `WebP (${comprehensive.images.webp || 0})`}
                          </button>
                        </div>
                      )}
                      
                      {/* Image Format Analysis */}
                      {comprehensive.images.nonWebp !== undefined && (
                        <div className="mb-3 pt-3 border-t border-slate-600">
                          <div className="text-xs font-semibold text-slate-300 mb-2">{t('results.imageFormatAnalysis') || 'Image Format Analysis:'}</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div className="bg-slate-700/50 p-2 rounded">
                              <div className="text-emerald-400 font-bold">{comprehensive.images.webp || 0}</div>
                              <div className="text-slate-400">{t('results.webpOptimal') || 'WebP (Optimal)'}</div>
        </div>
                            <div className="bg-slate-700/50 p-2 rounded">
                              <div className="text-yellow-400 font-bold">{comprehensive.images.png || 0}</div>
                              <div className="text-slate-400">{t('results.png') || 'PNG'}</div>
      </div>
                            <div className="bg-slate-700/50 p-2 rounded">
                              <div className="text-yellow-400 font-bold">{(comprehensive.images.jpg || 0) + (comprehensive.images.jpeg || 0)}</div>
                              <div className="text-slate-400">{t('results.jpeg') || 'JPG/JPEG'}</div>
    </div>
                            <div className="bg-slate-700/50 p-2 rounded">
                              <div className="text-slate-400 font-bold">{comprehensive.images.nonWebp || 0}</div>
                              <div className="text-slate-400">{t('results.nonWebPFormat') || 'Non-WebP'}</div>
                            </div>
                          </div>
                          {comprehensive.images.nonWebp > 0 && (
                            <div className="mt-2 text-xs text-yellow-400">
                              ⚠️ {t('results.webpRecommendation', { count: String(comprehensive.images.nonWebp) }) || `${comprehensive.images.nonWebp} image(s) are using PNG/JPG format. Convert to WebP for 25-35% better compression and faster loading.`}
                            </div>
                          )}
                          
                          {/* List of Non-WebP Images */}
                          {comprehensive.images.allImages && comprehensive.images.allImages.filter((img: any) => 
                            img.format && img.format !== 'webp' && img.format !== 'svg' && img.format !== 'avif'
                          ).length > 0 && (
                            <div className="mt-4 pt-3 border-t border-slate-600">
                              <div className="text-xs font-semibold text-yellow-300 mb-2">
                                {t('results.imagesNotWebP') || 'Images Not Using WebP Format'}:
                              </div>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {comprehensive.images.allImages
                                  .filter((img: any) => img.format && img.format !== 'webp' && img.format !== 'svg' && img.format !== 'avif')
                                  .slice(0, 20)
                                  .map((img: any, idx: number) => (
                                    <div key={idx} className="bg-yellow-500/10 p-2 rounded border border-yellow-500/30 text-xs">
                                      <div className="font-mono text-yellow-200 break-all">{img.src}</div>
                                      <div className="text-yellow-400/70 mt-1">
                                        {t('results.format') || 'Format'}: {img.format?.toUpperCase() || 'Unknown'} • 
                                        {t('results.position') || 'Position'}: #{img.position || idx + 1}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {comprehensive.images.missingAlt === 0 ? (
                        <div className="flex items-center gap-2 text-green-600 font-semibold">
                          <CheckCircle size={16} />
                          {t('results.noAltMissing') || 'No ALT attributes are missing.'}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-red-600 font-semibold mb-3">
                            <AlertTriangle size={16} />
                            {comprehensive.images.missingAlt} {t('results.imagesMissingAlt') || 'image(s) missing ALT attributes'}.
                          </div>
                          
                          {/* Show WHICH images are missing alt - Filtered */}
                          {comprehensive.images.missingAltDetails && comprehensive.images.missingAltDetails.length > 0 && (
                            <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                              <div className="text-xs font-semibold text-slate-300 mb-2">
                                {t('results.imagesMissingAltText') || 'Images Missing Alt Text'}:
                              </div>
                              {(() => {
                                let filteredImages = comprehensive.images.missingAltDetails;
                                if (imageFilter === 'missingAlt') {
                                  filteredImages = comprehensive.images.missingAltDetails;
                                } else if (imageFilter === 'nonWebp') {
                                  filteredImages = comprehensive.images.allImages?.filter((img: any) => 
                                    img.format && img.format !== 'webp' && img.format !== 'svg' && img.format !== 'avif' && !img.hasAlt
                                  ) || [];
                                } else if (imageFilter === 'webp') {
                                  filteredImages = comprehensive.images.allImages?.filter((img: any) => 
                                    img.format === 'webp' && !img.hasAlt
                                  ) || [];
                                } else {
                                  filteredImages = comprehensive.images.allImages?.filter((img: any) => !img.hasAlt) || comprehensive.images.missingAltDetails;
                                }
                                return filteredImages.map((img: any, idx: number) => (
                                <div key={idx} className="bg-red-500/10 p-3 rounded-lg border border-red-500/30">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-200 rounded overflow-hidden">
                                      <img 
                                        src={img.src} 
                                        alt="" 
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>';
                                        }}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-mono text-white break-all mb-1">
                                        {img.src}
                                      </div>
                                      <div className="flex gap-2 text-xs flex-wrap">
                                        <span className="text-red-600 font-semibold">{t('results.noAltText') || 'No alt text'}</span>
                                        <span className="text-slate-400">#{img.position || idx + 1}</span>
                                        {img.width && img.height && (
                                          <span className="text-slate-400">{img.width}×{img.height}</span>
                                        )}
                                        {img.loading === 'lazy' && (
                                          <span className="text-green-600">{t('results.lazyLoaded') || 'Lazy loaded'} ✓</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ));
                              })()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Additional image statistics */}
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div className="bg-slate-800 p-3 rounded border border-slate-600 text-center">
                        <div className="text-xs text-slate-300 mb-1">{t('results.withAlt') || 'With Alt'}</div>
                        <div className="text-lg font-bold text-green-600">
                          {comprehensive.images.withAlt || 0}
                        </div>
                      </div>
                      <div className="bg-slate-800 p-3 rounded border border-slate-600 text-center">
                        <div className="text-xs text-slate-300 mb-1">{t('results.lazyLoading') || 'Lazy Loading'}</div>
                        <div className="text-lg font-bold text-blue-400">
                          {comprehensive.images.withLazyLoading || 0}
                        </div>
                      </div>
                      <div className="bg-slate-800 p-3 rounded border border-slate-600 text-center">
                        <div className="text-xs text-slate-300 mb-1">{t('results.responsive') || 'Responsive'}</div>
                        <div className="text-lg font-bold text-indigo-400">
                          {comprehensive.images.withSrcset || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Language Detection */}
                {comprehensive.language && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {t('results.language') || 'Language'}
                    </label>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('results.declared') || 'Declared'}</div>
                          <div className="flex items-center gap-2">
                            {comprehensive.language.declared && comprehensive.language.declared.includes('-') && (
                              <span className="text-2xl">
                                {(() => {
                                  const langCode = comprehensive.language.declared.split('-')[0].toLowerCase();
                                  const countryCode = comprehensive.language.declared.split('-')[1]?.toUpperCase() || '';
                                  // Return flag emoji based on country code
                                  const flagEmojis: Record<string, string> = {
                                    'US': '🇺🇸', 'GB': '🇬🇧', 'FR': '🇫🇷', 'DE': '🇩🇪', 'ES': '🇪🇸',
                                    'IT': '🇮🇹', 'PT': '🇵🇹', 'NL': '🇳🇱', 'PL': '🇵🇱', 'RU': '🇷🇺',
                                    'CN': '🇨🇳', 'JP': '🇯🇵', 'KR': '🇰🇷', 'IN': '🇮🇳', 'BR': '🇧🇷',
                                    'MX': '🇲🇽', 'AR': '🇦🇷', 'CA': '🇨🇦', 'AU': '🇦🇺', 'NZ': '🇳🇿',
                                    'SA': '🇸🇦', 'AE': '🇦🇪', 'EG': '🇪🇬', 'MA': '🇲🇦', 'DZ': '🇩🇿',
                                    'TN': '🇹🇳', 'TR': '🇹🇷', 'GR': '🇬🇷', 'SE': '🇸🇪', 'NO': '🇳🇴',
                                    'DK': '🇩🇰', 'FI': '🇫🇮', 'BE': '🇧🇪', 'CH': '🇨🇭', 'AT': '🇦🇹'
                                  };
                                  return flagEmojis[countryCode] || '🌐';
                                })()}
                              </span>
                            )}
                            <span className="text-white font-semibold">{comprehensive.language.declared || t('results.notSpecified') || 'Not specified'}</span>
             </div>
                </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('results.detected') || 'Detected'}</div>
                          <div className="flex items-center gap-2">
                            {comprehensive.language.detected && comprehensive.language.detected.includes('-') && (
                              <span className="text-2xl">
                                {(() => {
                                  const langCode = comprehensive.language.detected.split('-')[0].toLowerCase();
                                  const countryCode = comprehensive.language.detected.split('-')[1]?.toUpperCase() || '';
                                  const flagEmojis: Record<string, string> = {
                                    'US': '🇺🇸', 'GB': '🇬🇧', 'FR': '🇫🇷', 'DE': '🇩🇪', 'ES': '🇪🇸',
                                    'IT': '🇮🇹', 'PT': '🇵🇹', 'NL': '🇳🇱', 'PL': '🇵🇱', 'RU': '🇷🇺',
                                    'CN': '🇨🇳', 'JP': '🇯🇵', 'KR': '🇰🇷', 'IN': '🇮🇳', 'BR': '🇧🇷',
                                    'MX': '🇲🇽', 'AR': '🇦🇷', 'CA': '🇨🇦', 'AU': '🇦🇺', 'NZ': '🇳🇿',
                                    'SA': '🇸🇦', 'AE': '🇦🇪', 'EG': '🇪🇬', 'MA': '🇲🇦', 'DZ': '🇩🇿',
                                    'TN': '🇹🇳', 'TR': '🇹🇷', 'GR': '🇬🇷', 'SE': '🇸🇪', 'NO': '🇳🇴',
                                    'DK': '🇩🇰', 'FI': '🇫🇮', 'BE': '🇧🇪', 'CH': '🇨🇭', 'AT': '🇦🇹'
                                  };
                                  return flagEmojis[countryCode] || '🌐';
                                })()}
                              </span>
                            )}
                            <span className="text-indigo-400 font-semibold">{comprehensive.language.detected || comprehensive.language.declared || 'Unknown'}</span>
                </div>
             </div>
          </div>
                    </div>
                  </div>
                )}

                {/* Links */}
                {comprehensive.links && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <label className="text-sm font-bold text-slate-200 block mb-3 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      {t('results.inPageLinks') || 'In-Page Links'}
                    </label>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="text-sm text-slate-200 mb-3">
                        {t('results.foundTotalLinks') || 'We found a total of'} {formatNumber(comprehensive.links.total)} {t('results.linksIncluding') || 'link(s) including'} {formatNumber(comprehensive.links.fileLinks)} {t('results.linksToFiles') || 'link(s) to files'}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
                        <div className="bg-blue-500/10 p-3 rounded border border-blue-500/30">
                          <div className="font-semibold text-blue-200">{t('results.internalLinks') || 'Internal Links'}</div>
                          <div className="text-2xl font-bold text-blue-400">{comprehensive.links.internal}</div>
                          <div className="text-xs text-blue-400">({Math.round((comprehensive.links.internal / comprehensive.links.total) * 100)}%)</div>
                        </div>
                        <div className="bg-green-500/10 p-3 rounded border border-green-500/30">
                          <div className="font-semibold text-green-200">{t('results.externalLinksFollow') || 'External Links: Follow'}</div>
                          <div className="text-2xl font-bold text-green-600">{comprehensive.links.external}</div>
                          <div className="text-xs text-green-600">({Math.round((comprehensive.links.external / comprehensive.links.total) * 100)}%)</div>
                        </div>
                        <div className="bg-orange-600/20 p-3 rounded border border-orange-500/40">
                          <div className="font-semibold text-orange-100">{t('results.externalLinksNoFollow') || 'External Links: NoFollow'}</div>
                          <div className="text-2xl font-bold text-orange-300">{comprehensive.links.nofollow}</div>
                          <div className="text-xs text-orange-200">({Math.round((comprehensive.links.nofollow / comprehensive.links.total) * 100)}%)</div>
                        </div>
                      </div>
                      
                      {/* Links Table */}
                      {comprehensive.links.details && comprehensive.links.details.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-600">
                          <div className="text-xs font-semibold text-slate-300 mb-3">{t('results.linkDetails') || 'Link Details:'}</div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-600">
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">#</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.titleAnchor') || 'Title/Anchor'}</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.url') || 'URL'}</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.type') || 'Type'}</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.follow') || 'Follow'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {comprehensive.links.details.slice(0, 50).map((link: any, idx: number) => (
                                  <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                    <td className="py-2 px-3 text-slate-400">{idx + 1}</td>
                                    <td className="py-2 px-3 text-slate-200 max-w-xs truncate" title={link.anchor || link.href}>
                                      {link.anchor || '(no text)'}
                                    </td>
                                    <td className="py-2 px-3 text-slate-300 max-w-md truncate font-mono text-xs" title={link.href}>
                                      {link.href}
                                    </td>
                                    <td className="py-2 px-3">
                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                        link.type === 'internal' ? 'bg-blue-500/20 text-blue-300' :
                                        link.type === 'external' ? 'bg-green-500/20 text-green-300' :
                                        link.type === 'nofollow' ? 'bg-orange-500/20 text-orange-300' :
                                        'bg-slate-500/20 text-slate-300'
                                      }`}>
                                        {link.type === 'internal' ? (t('results.internal') || 'internal') :
                                         link.type === 'external' ? (t('results.external') || 'external') :
                                         link.type}
              </span>
                                    </td>
                                    <td className="py-2 px-3">
                                      {link.follow ? (
                                        <span className="text-green-400">✓</span>
                                      ) : (
                                        <span className="text-red-400">✗</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
            </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 4. TECHNICAL SEO (Collapsible) */}
        {comprehensive?.technical && (
          <div className="bg-slate-800 rounded-xl border border-slate-600 p-5 mb-6 shadow-sm transition-all hover:border-indigo-500/50">
               <button 
              onClick={() => toggleSection('technical')}
              className="w-full flex items-center justify-between text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
                  <Cpu className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-white">{t('results.categoryTechnical') || 'Technical SEO'}</h2>
                  {safeIssues.filter(i => i.category === Category.TECHNICAL).length > 0 && (
                    <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                      {safeIssues.filter(i => i.category === Category.TECHNICAL).length}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {safeCategories[Category.TECHNICAL]?.score !== undefined && (
                  <span className={`text-sm font-bold ${
                    (safeCategories[Category.TECHNICAL]?.score || 0) >= 80 ? 'text-emerald-400' :
                    (safeCategories[Category.TECHNICAL]?.score || 0) >= 60 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {safeCategories[Category.TECHNICAL]?.score || 0}/100
                  </span>
                )}
                {expandedSections.technical ? (
                  <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                ) : (
                  <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                )}
              </div>
               </button>
            
            {expandedSections.technical && (
              <div className="mt-4 space-y-4">
                {/* Indexing */}
                <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <FileSearch className="w-4 h-4" />
                    {t('results.indexing') || 'Indexing'}
                  </h3>
                  <div className="space-y-2">
                    {comprehensive.technical.robotsTxt && (
                      <div className="flex items-center justify-between bg-slate-800 p-3 rounded border border-slate-600">
                        <span className="text-sm text-slate-200">{t('results.robotsTxt') || 'Robots.txt'}</span>
                        <span className={comprehensive.technical.robotsTxt.exists ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {comprehensive.technical.robotsTxt.exists ? t('results.found') || 'Found' : t('results.notFound') || 'Not Found'}
                        </span>
            </div>
                    )}
                    {comprehensive.technical.sitemap && (
                      <div className="flex items-center justify-between bg-slate-800 p-3 rounded border border-slate-600">
                        <span className="text-sm text-slate-200">{t('results.sitemap') || 'Sitemap'}</span>
                        <span className={comprehensive.technical.sitemap.exists ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {comprehensive.technical.sitemap.exists ? t('results.found') || 'Found' : t('results.notFound') || 'Not Found'}
                        </span>
          </div>
                    )}
                    {comprehensive.technical.canonical && (
                      <div className="flex items-center justify-between bg-slate-800 p-3 rounded border border-slate-600">
                        <span className="text-sm text-slate-200">{t('results.canonical') || 'Canonical Tag'}</span>
                        <span className={comprehensive.technical.canonical ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {comprehensive.technical.canonical ? t('results.present') || 'Present' : t('results.missing') || 'Missing'}
                        </span>
        </div>
                    )}
                    
                    {/* URL Resolution */}
                    {comprehensive.indexing?.urlResolve && comprehensive.indexing.urlResolve.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-600">
                        <div className="text-xs font-semibold text-slate-300 mb-2">{t('results.urlResolution') || 'URL Resolution'}:</div>
                        <div className="bg-slate-800 p-3 rounded border border-slate-600">
                          <div className="text-xs text-green-400 mb-2">
                            ✓ {t('results.urlResolutionGood') || 'Great, a redirect is in place to redirect traffic from your non-preferred domain.'}
                </div>
                          <div className="text-xs text-slate-300 mb-3">
                            {t('results.allVersionsSameUrl') || 'All versions of your page point to the same URL.'}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-slate-600">
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.url') || 'URL'}</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.resolvedUrl') || 'Resolved URL'}</th>
                                  <th className="text-left py-2 px-3 text-slate-300 font-semibold">{t('results.redirectType') || 'Redirect Type'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {comprehensive.indexing.urlResolve.map((resolve: any, idx: number) => (
                                  <tr key={idx} className="border-b border-slate-700/50">
                                    <td className="py-2 px-3 text-slate-300 font-mono text-xs break-all">{resolve.url || resolve.from || 'N/A'}</td>
                                    <td className="py-2 px-3 text-green-400 font-mono text-xs break-all">{resolve.resolvedUrl || resolve.resolved || resolve.to || 'N/A'}</td>
                                    <td className="py-2 px-3 text-slate-400 text-xs">{resolve.redirectType || resolve.type || (t('results.direct') || 'Direct')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-2 text-xs text-yellow-400">
                            ⚠️ {t('results.wwwNonWwwWarning') || 'Search engines see www and non-www as different websites. This means they could see duplicate content, which they don\'t like.'}
                          </div>
                          <div className="mt-1 text-xs text-green-400">
                            ✓ {t('results.wwwNonWwwGood') || 'Fortunately your website redirects www and non-www to the same site.'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
             </div>

                {/* Mobile Friendliness */}
                <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    {t('results.mobileFriendliness') || 'Mobile Friendliness'}
                  </h3>
                  <div className="bg-slate-800 p-4 rounded border border-slate-600 space-y-4">
                    {/* Viewport Status */}
                    <div>
                      {((comprehensive.mobile?.viewport?.present || comprehensive.mobile?.viewport?.exists) || 
                        (comprehensive.technical?.viewport?.present || comprehensive.technical?.viewport?.exists)) ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle size={16} />
                          <span>{t('results.mobileFriendly') || 'Mobile-friendly viewport detected'}</span>
                    </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle size={16} />
                          <span>{t('results.notMobileFriendly') || 'Mobile viewport not properly configured'}</span>
                    </div>
                      )}
                    </div>
                    
                    {/* Mobile Screenshot */}
                    {mobileScreenshot && (
                      <div className="mt-4">
                        <div className="text-xs font-semibold text-slate-300 mb-2">
                          {t('results.mobilePreview') || 'Mobile Preview'}
                  </div>
                        <div className="rounded-lg overflow-hidden border border-slate-600 bg-slate-900 p-2">
                          <img 
                            src={typeof mobileScreenshot === 'string' && mobileScreenshot.startsWith('data:')
                              ? mobileScreenshot
                              : `data:image/png;base64,${mobileScreenshot}`} 
                            alt="Mobile website preview" 
                            className="w-full h-auto max-h-96 object-contain mx-auto"
                            style={{ maxWidth: '375px' }}
                            onError={(e) => {
                              console.error('[ResultsDashboard] Mobile screenshot failed to load');
                              const target = e.target as HTMLImageElement;
                              if (target.parentElement) {
                                target.parentElement.style.display = 'none';
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Security */}
                <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {t('results.security') || 'Security'}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-slate-800 p-3 rounded border border-slate-600">
                      <span className="text-sm text-slate-200">{t('results.https') || 'HTTPS'}</span>
                      <span className={safeUrl.startsWith('https') ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {safeUrl.startsWith('https') ? t('results.enabled') || 'Enabled' : t('results.disabled') || 'Disabled'}
                      </span>
             </div>
                    {comprehensive.technical.dmarc && (
                      <div className="flex items-center justify-between bg-slate-800 p-3 rounded border border-slate-600">
                        <span className="text-sm text-slate-200">{t('results.dmarc') || 'DMARC Record'}</span>
                        <span className={comprehensive.technical.dmarc.exists ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {comprehensive.technical.dmarc.exists ? t('results.found') || 'Found' : t('results.missing') || 'Missing'}
                        </span>
                </div>
                    )}
                </div>
             </div>

                {/* Structured Data */}
                {comprehensive.technical.structuredData && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      {t('results.structuredData') || 'Structured Data'}
                    </h3>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      <div className="text-sm text-slate-200">
                        {t('results.schemaTypesFound') || 'Schema types found'}: {comprehensive.technical.structuredData.types?.length || 0}
          </div>
                      {comprehensive.technical.structuredData.types && comprehensive.technical.structuredData.types.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {comprehensive.technical.structuredData.types.map((type: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-semibold">
                              {type}
              </span>
                ))}
             </div>
                      )}
          </div>
                  </div>
                )}

                {/* HEAD Analysis */}
                {comprehensive.headAnalysis && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('results.headSectionAnalysis') || 'HEAD Section Analysis'}
                    </h3>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-200">{t('results.score') || 'Score'}</span>
                        <span className={`text-lg font-bold ${comprehensive.headAnalysis.score >= 80 ? 'text-green-400' : comprehensive.headAnalysis.score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {comprehensive.headAnalysis.score}/100
                        </span>
             </div>
                      <div className="text-xs text-slate-400 mt-2">
                        {t('results.issuesFound', { count: String(comprehensive.headAnalysis.issues?.length || 0) })}
          </div>
                      {comprehensive.headAnalysis.issues && comprehensive.headAnalysis.issues.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comprehensive.headAnalysis.issues.slice(0, 3).map((issue: any, idx: number) => (
                            <div key={idx} className="text-xs text-slate-300 bg-slate-700/50 p-2 rounded">
                              • {issue.message || issue}
        </div>
                          ))}
      </div>
                      )}
                    </div>
                  </div>
                )}

                {/* FOOTER Analysis */}
                {comprehensive.footerAnalysis && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('results.footerSectionAnalysis') || 'FOOTER Section Analysis'}
          </h3>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-200">{t('results.score') || 'Score'}</span>
                        <span className={`text-lg font-bold ${comprehensive.footerAnalysis.score >= 80 ? 'text-green-400' : comprehensive.footerAnalysis.score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {comprehensive.footerAnalysis.score}/100
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 mt-2">
                        {t('results.issuesFound', { count: String(comprehensive.footerAnalysis.issues?.length || 0) })}
                      </div>
                      {comprehensive.footerAnalysis.issues && comprehensive.footerAnalysis.issues.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comprehensive.footerAnalysis.issues.slice(0, 3).map((issue: any, idx: number) => (
                            <div key={idx} className="text-xs text-slate-300 bg-slate-700/50 p-2 rounded">
                              • {issue.message || issue}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Harmony Score */}
                {comprehensive.harmonyScore && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {t('results.harmonyScore') || 'HEAD/FOOTER Harmony Score'}
                    </h3>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-200">{t('results.overallHarmony') || 'Overall Harmony'}</span>
                        <span className={`text-lg font-bold ${comprehensive.harmonyScore.score >= 80 ? 'text-green-400' : comprehensive.harmonyScore.score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {comprehensive.harmonyScore.score}/100
                        </span>
                      </div>
                      {comprehensive.harmonyScore.recommendations && comprehensive.harmonyScore.recommendations.length > 0 && (
                        <div className="text-xs text-slate-400 mt-2">
                          {t('results.recommendations') || 'Recommendations'}: {comprehensive.harmonyScore.recommendations.length}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 5. PERFORMANCE (Collapsible) */}
        {report.performanceAudit && (
          <div id="section-performance" className="bg-slate-800 rounded-xl border border-slate-600 p-5 mb-6 shadow-sm transition-all hover:border-yellow-500/50">
            <button
              onClick={() => toggleSection('performance')}
              className="w-full flex items-center justify-between text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg group-hover:bg-yellow-500/30 transition-colors">
                  <Zap className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-white">{t('performanceAudit.title') || 'Performance Audit'}</h2>
                  {safeIssues.filter(i => i.category === 'Performance').length > 0 && (
                    <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                      {safeIssues.filter(i => i.category === 'Performance').length}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${
                  performanceScore >= 80 ? 'text-emerald-400' :
                  performanceScore >= 60 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {performanceScore}/100
                </span>
                {expandedSections.performance ? (
                  <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                ) : (
                  <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                )}
              </div>
            </button>
            
            {expandedSections.performance && report.performanceAudit && (
              <div className="mt-4 space-y-4">
                {/* Asset Minification */}
                {report.performanceAudit.minification && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      {t('performanceAudit.minification.title') || 'Asset Minification (CSS/JS)'}
                    </h3>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.minification.cssFiles') || 'CSS Files'}</div>
                          <div className="text-white font-semibold">{report.performanceAudit.minification.css?.length || 0} {t('results.total') || 'total'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.minification.jsFiles') || 'JS Files'}</div>
                          <div className="text-white font-semibold">{report.performanceAudit.minification.js?.length || 0} {t('results.total') || 'total'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.minification.totalSavings') || 'Potential Savings'}</div>
                          <div className="text-indigo-400 font-semibold">{report.performanceAudit.minification.totalSavingsFormatted || '0 KB'}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('results.status') || 'Status'}</div>
                          {report.performanceAudit.minification.css?.filter((f: any) => !f.isMinified).length === 0 && 
                           report.performanceAudit.minification.js?.filter((f: any) => !f.isMinified).length === 0 ? (
                            <div className="text-green-400 font-semibold">✓ {t('results.allMinified') || 'All Minified'}</div>
                          ) : (
                            <div className="text-yellow-400 font-semibold">⚠ {t('results.needsOptimization') || 'Needs Optimization'}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Browser Caching */}
                {report.performanceAudit.cacheability && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      {t('performanceAudit.cacheability.title') || 'Browser Caching'}
                    </h3>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-600">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.cacheability.totalAssets') || 'Total Assets'}</div>
                          <div className="text-white font-semibold">{report.performanceAudit.cacheability.totalAssets || 0}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.cacheability.cached') || 'Cached'}</div>
                          <div className="text-green-400 font-semibold">{report.performanceAudit.cacheability.cached || 0}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs mb-1">{t('performanceAudit.cacheability.uncached') || 'Not Cached'}</div>
                          <div className="text-red-400 font-semibold">{report.performanceAudit.cacheability.uncached || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 6. LINK ANALYSIS (Collapsible) */}
        {report.linkAnalysis && (
          <div id="section-links" className="bg-slate-800 rounded-xl border border-slate-600 p-5 mb-6 shadow-sm transition-all hover:border-blue-500/50">
              <button
              onClick={() => toggleSection('links')}
              className="w-full flex items-center justify-between text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                  <Link2 className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-white">{t('linkAnalysis.title') || 'Link Analysis'}</h2>
                  {(report.linkAnalysis.brokenLinks?.length || 0) > 0 && (
                    <span className="px-2.5 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                      {report.linkAnalysis.brokenLinks?.length || 0}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {expandedSections.links ? (
                  <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                ) : (
                  <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
                )}
              </div>
              </button>
            
            {expandedSections.links && report.linkAnalysis && (
              <div className="mt-4 space-y-4">
                {/* Broken Links */}
                <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    {t('linkAnalysis.brokenLinks') || 'Broken Links (404 Errors)'}
                  </h3>
                  {report.linkAnalysis.brokenLinks && report.linkAnalysis.brokenLinks.length > 0 ? (
                    <div className="space-y-2">
                      {report.linkAnalysis.brokenLinks.map((link, idx) => (
                        <div key={idx} className="bg-red-500/10 p-3 rounded-lg border border-red-500/30">
                          <div className="text-sm font-semibold text-red-200 mb-1 break-all">{link.url}</div>
                          <div className="text-xs text-red-600">
                            {t('linkAnalysis.status') || 'Status'}: {link.statusCode} - {link.statusText}
            </div>
                          {link.anchorText && (
                            <div className="text-xs text-slate-300 mt-1">
                              {t('linkAnalysis.anchor') || 'Anchor'}: "{link.anchorText}"
          </div>
                          )}
        </div>
            ))}
          </div>
                  ) : (
                    <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-300 font-semibold">{t('results.noBrokenLinks') || 'No broken links found!'}</p>
                    </div>
                  )}
        </div>
        
                {/* Redirects */}
                <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                  <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-yellow-500" />
                    {t('linkAnalysis.redirects') || 'Redirects (301/302)'}
                  </h3>
                  {report.linkAnalysis.redirects && report.linkAnalysis.redirects.length > 0 ? (
                    <div className="space-y-2">
                      {report.linkAnalysis.redirects.map((redirect, idx) => (
                        <div key={idx} className="bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/30">
                          <div className="text-sm font-semibold text-yellow-200 mb-1">
                            {redirect.redirectType} ({redirect.statusCode})
                    </div>
                          <div className="text-xs text-yellow-700">
                            {t('linkAnalysis.from') || 'From'}: {redirect.from.substring(0, 60)}...
                    </div>
                          <div className="text-xs text-yellow-700">
                            {t('linkAnalysis.to') || 'To'}: {redirect.to.substring(0, 60)}...
                    </div>
                  </div>
                ))}
             </div>
                  ) : (
                    <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-300 font-semibold">{t('results.noRedirects') || 'No redirects detected!'}</p>
                    </div>
                  )}
          </div>

                {/* Sitemap */}
                {report.linkAnalysis.sitemap && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <h3 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                      <FileSearch className="w-4 h-4" />
                      {t('linkAnalysis.sitemapFound') || 'Sitemap Status'}
                    </h3>
                    <div className="bg-slate-800 p-3 rounded border border-slate-600">
                      {report.linkAnalysis.sitemap.exists ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle size={16} />
                          <span>{t('linkAnalysis.sitemapFound') || 'Sitemap Found'}</span>
                          {report.linkAnalysis.sitemap.url && (
                            <span className="text-xs text-slate-400 ml-2">({report.linkAnalysis.sitemap.url})</span>
                          )}
             </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle size={16} />
                          <span>{t('linkAnalysis.sitemapNotFound') || 'Sitemap Not Found'}</span>
          </div>
                      )}
        </div>
      </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 7. ACTION PLAN - Enhanced with new component */}
        <ActionPlanSection
          issues={sortedIssues}
          isExpanded={expandedSections.actionPlan}
          onToggle={() => toggleSection('actionPlan')}
        />

        {/* 8. AI-REORGANIZED VIEW (Collapsible) */}
        {reorganized && reorganized.categories && reorganized.categories.length > 0 && (
          <div className="bg-slate-800 rounded-xl border border-indigo-500/30 p-5 mb-6 shadow-sm transition-all hover:border-indigo-500/50">
            <button
              onClick={() => toggleSection('reorganized')}
              className="w-full flex items-center justify-between text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg group-hover:bg-indigo-500/30 transition-colors">
                  <Bot className="w-5 h-5 text-indigo-400" />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-white">{t('results.aiOrganized') || 'AI-Organized Audit Results'}</h2>
                  <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">{t('results.industryStandard') || 'Industry Standard'}</span>
                </div>
              </div>
              {expandedSections.reorganized ? (
                <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
              ) : (
                <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
              )}
            </button>
            
            {expandedSections.reorganized && (
              <div className="mt-4 space-y-6">
                {/* Reorganized Categories */}
                {reorganized.categories.map((category: any, catIdx: number) => (
                  <div key={catIdx} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <span>{category.icon || '📋'}</span>
                        {category.name}
                      </h3>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-400">{category.totalIssues} {category.totalIssues === 1 ? (t('results.issue') || 'issue') : (t('results.issues') || 'issues')}</span>
                        <span className={`text-lg font-bold ${
                          category.score >= 80 ? 'text-green-400' : 
                          category.score >= 60 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>
                          {category.score}/100
                        </span>
              </div>
            </div>
                    
                    {category.description && (
                      <p className="text-sm text-slate-300 mb-4">{category.description}</p>
                    )}
                    
                    {/* Subcategories */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="space-y-3">
                        {category.subcategories.map((subcat: any, subIdx: number) => (
                          <div key={subIdx} className="bg-slate-800 rounded-lg p-3 border border-slate-600">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-slate-200">{subcat.name}</h4>
                              <span className="text-xs text-slate-400">{subcat.totalIssues || 0} {(subcat.totalIssues || 0) === 1 ? (t('results.issue') || 'issue') : (t('results.issues') || 'issues')}</span>
        </div>
                            
                            {/* Issues in this subcategory */}
                            {subcat.issues && subcat.issues.length > 0 && (
                              <div className="space-y-2 mt-3">
                                {subcat.issues.slice(0, 5).map((issue: any, issueIdx: number) => (
                                  <IssueCard key={`${catIdx}-${subIdx}-${issueIdx}`} issue={issue} index={issueIdx} />
                                ))}
                                {subcat.issues.length > 5 && (
                                  <div className="text-xs text-slate-400 text-center pt-2">
                                    +{subcat.issues.length - 5} {t('results.moreIssues') || 'more issues'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Radar Chart - Only if we have visible categories */}
        {visibleCategories.length > 0 && (
          <div id="section-radar" className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6 shadow-sm transition-all hover:border-indigo-500/50 print:hidden">
            <button
              onClick={() => toggleSection('radar')}
              className="w-full flex items-center justify-between text-left group mb-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg group-hover:bg-indigo-500/30 transition-colors">
                  <BarChart3 className="w-5 h-5 text-indigo-400" />
                </div>
                <h2 className="text-xl font-bold text-white">{t('results.overallPerformance') || 'Overall Performance'}</h2>
              </div>
              {expandedSections.radar ? (
                <ChevronUp className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
              ) : (
                <ChevronDown className="text-slate-400 group-hover:text-indigo-400 transition-colors" size={20} />
              )}
            </button>
            {expandedSections.radar && (
              <div style={{ minHeight: '350px', width: '100%' }}>
              <ResponsiveContainer width="100%" height={350} minHeight={350}>
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
                  <PolarGrid 
                    stroke="#475569" 
                    strokeWidth={1}
                    strokeDasharray="3 3"
                  />
                  <PolarAngleAxis 
                    dataKey="subject" 
                    tick={{ fill: '#cbd5e1', fontSize: 11, fontWeight: 600 }}
                    tickLine={{ stroke: '#64748b' }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]} 
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    tickCount={6}
                    axisLine={{ stroke: '#475569' }}
                  />
                  <Radar
                    name={t('results.score') || 'Score'}
                    dataKey="A"
                    stroke="#818cf8"
                    strokeWidth={2.5}
                    fill="#818cf8"
                    fillOpacity={0.25}
                    dot={{ fill: '#818cf8', r: 4 }}
                    activeDot={{ r: 6, fill: '#a5b4fc' }}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
                            <p className="text-white font-semibold text-sm">{payload[0].payload.subject}</p>
                            <p className="text-indigo-400 font-bold text-lg">{payload[0].value}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
